/**
 * The route table.
 *
 * ONE shell, wrapping everything that has chrome, with the guards nested
 * INSIDE it as pathless layout routes rather than wrapped around it. That
 * placement is the whole point: giving each group its own
 * <ProtectedRoute><AppShell/></ProtectedRoute> put them in separate branches,
 * and React Router tears down the branch it is leaving — so a signed-in admin
 * clicking Standings lost the entire sidebar and had no way back to where they
 * were. Nested this way, AppShell is one mount for the life of the session.
 *
 * AppShell is NOT itself guarded. An anonymous visitor reaching a public venue
 * page gets the shell with a public-only nav; the guard applies to the routes
 * it wraps, not to the chrome around them.
 *
 * Outside the shell, deliberately:
 *
 *   /        Landing owns its whole viewport — its own bar, hero and footer.
 *   /login   .hd-auth is a centred column with no chrome at all.
 *   /signup  Same column, same reason.
 *   /verify-email  Same again, and deliberately UNGUARDED — the token in the
 *            link is the credential, and it is usually opened in a phone's
 *            mail app where the site has no cookies.
 *   /board   Full-screen surfaces. A sidebar on a wall-mounted screen is
 *   /scorer  wasted pixels, and the scorer is used at arm's length on a
 *   /tv      tablet. /board and /scorer are NOT guarded: the scorer runs on a
 *            shared tablet nobody signs into, authorised by the tournament it
 *            is paired to, server-side — a login gate there would put a
 *            password prompt on a pub wall. /tv IS guarded, to venue_admin and
 *            above, because nothing pairs it to a tournament and so nothing
 *            else authorises it. V1 gates its TV page the same way.
 *
 *   /pair    Guarded, and NOT full-screen — it is a form on somebody's phone.
 *            See the note at the route itself.
 *
 * /board carries no venue, deliberately: whoever sets a tablet up types one
 * flat URL and the venue is decided by which admin scans the code. There is
 * also NO shortcut anywhere in the app that opens a board's scorer directly.
 * V1 had one on its dashboard, and because it relied on localStorage left
 * behind by an already-paired device it was a dead end from any other — a link
 * that looks like it works and cannot. A scorer is reachable by pairing, or
 * not at all.
 *
 * Route ORDER matters where paths overlap. /venues/:venueSlug/tournaments is
 * public while /venues/:venueSlug/admin/tournaments is not, so the literal
 * `admin` segment has to be a distinct branch rather than a parameter that
 * could swallow it.
 */

import { Navigate, Routes, Route } from 'react-router-dom';

import AppShell        from './components/AppShell.jsx';
import ProtectedRoute  from './components/ProtectedRoute.jsx';

import Landing           from './pages/Landing.jsx';
import RegisterVenue     from './pages/RegisterVenue.jsx';
import Login             from './pages/Login.jsx';
import SignUp            from './pages/SignUp.jsx';
import VerifyEmail       from './pages/VerifyEmail.jsx';
import VenueLanding      from './pages/VenueLanding.jsx';
import PublicStandings   from './pages/PublicStandings.jsx';
import PublicLive        from './pages/PublicLive.jsx';
import TournamentRegister from './pages/TournamentRegister.jsx';
import TournamentResults  from './pages/TournamentResults.jsx';
import TournamentSummary from './pages/TournamentSummary.jsx';
import MatchDetail       from './pages/MatchDetail.jsx';
import HallOfFame        from './pages/HallOfFame.jsx';
import PublicProfile     from './pages/PublicProfile.jsx';

import Dashboard         from './pages/Dashboard.jsx';
import AdminDashboard    from './pages/AdminDashboard.jsx';
import AdminTournaments  from './pages/AdminTournaments.jsx';
import AdminTournamentDetail from './pages/AdminTournamentDetail.jsx';
import AdminPlayers      from './pages/AdminPlayers.jsx';
import AdminSeasons      from './pages/AdminSeasons.jsx';
import AdminSettings     from './pages/AdminSettings.jsx';
import AdminTvSettings   from './pages/AdminTvSettings.jsx';
import AdminCharity      from './pages/AdminCharity.jsx';
import MyStats           from './pages/MyStats.jsx';
import Profile          from './pages/Profile.jsx';

import Board             from './pages/Board.jsx';
import Pair              from './pages/Pair.jsx';
import Scorer            from './pages/Scorer.jsx';
import Tv                from './pages/Tv.jsx';

import PlatformVenues    from './pages/PlatformVenues.jsx';
import PlatformVenueDetail from './pages/PlatformVenueDetail.jsx';
import PlatformSettings  from './pages/PlatformSettings.jsx';

import NotFound          from './pages/NotFound.jsx';

export default function AppRoutes() {
  return (
    <Routes>
      {/* ---- The shell — sidebar, topbar, content column ------------------ */}
      <Route element={<AppShell />}>

        {/* ---- Public — no guard ---------------------------------------- */}
        <Route path="/register-venue" element={<RegisterVenue />} />
        <Route path="/venues/:venueSlug" element={<VenueLanding />} />
        <Route path="/venues/:venueSlug/standings" element={<PublicStandings />} />
        {/* The list AND the way into it. TournamentRegister took this route
            from PublicTournaments, which listed the same nights and could
            not enter one — registering was a placeholder, and AccountPrompt
            had been built for a page that did not exist yet. */}
        <Route path="/venues/:venueSlug/tournaments" element={<TournamentRegister />} />
        <Route path="/venues/:venueSlug/tournaments/:id/live" element={<PublicLive />} />
        {/* Kept as a redirect rather than deleted: it is live on staging and
            may be linked or bookmarked. Registering now happens on the list
            itself, so there is nothing at the old path to land on.

            '../..' is two segments up from .../tournaments/:id/register — one
            would only reach .../tournaments/:id, which is not a route. */}
        <Route
          path="/venues/:venueSlug/tournaments/:id/register"
          element={<Navigate to="../.." relative="path" replace />}
        />
        <Route path="/venues/:venueSlug/tournaments/:id/results" element={<TournamentResults />} />
        {/* The drill-down chain: one player's night, then one match out of
            it. Public, like the endpoints behind them and like /results
            above — a spectator reads a finished night off their phone.
            Scoped to an ENTRY rather than an account, because a night
            belongs to the entry that played it, which is also what makes
            these pages work for a guest. */}
        <Route path="/venues/:venueSlug/tournaments/:id/player/:entryId" element={<TournamentSummary />} />
        <Route path="/venues/:venueSlug/tournaments/:id/match/:matchId" element={<MatchDetail />} />
        <Route path="/venues/:venueSlug/hall-of-fame" element={<HallOfFame />} />
        <Route path="/venues/:venueSlug/players/:id" element={<PublicProfile />} />

        {/* ---- Authenticated — a player's own pages --------------------- */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Venue-agnostic, deliberately: an account belongs to a person, and
              a player with no current venue still needs somewhere to land.
              Post-signup this is always the destination. */}
          <Route path="/profile" element={<Profile />} />
          {/* The old venue-scoped path, kept as a redirect rather than deleted:
              it is live on staging and may be bookmarked or linked. Inside the
              guard so an anonymous visitor hitting it still ends up signed in
              and then here, rather than bouncing twice. */}
          <Route
            path="/venues/:venueSlug/profile"
            element={<Navigate to="/profile" replace />}
          />
          {/* Stays venue-scoped — these are per-venue stats, not account
              settings. */}
          <Route path="/venues/:venueSlug/my-stats" element={<MyStats />} />
        </Route>

        {/* ---- Authenticated — the venue console, admins only -----------
          * Matches the `admin: true` filter AppShell applies to the same set
          * of links, so a player never sees an entry its route then refuses.
          * Enforcement is still server-side; this only stops the page from
          * rendering and then 401ing on every call it makes. */}
        <Route element={<ProtectedRoute venueAdmin />}>
          <Route path="/venues/:venueSlug/admin" element={<AdminDashboard />} />
          <Route path="/venues/:venueSlug/admin/tournaments" element={<AdminTournaments />} />
          {/* Sits after the list so the literal path wins; :tournamentId cannot
              swallow it either way, but the order matches the `admin` note above. */}
          <Route path="/venues/:venueSlug/admin/tournaments/:tournamentId" element={<AdminTournamentDetail />} />
          <Route path="/venues/:venueSlug/admin/players" element={<AdminPlayers />} />
          <Route path="/venues/:venueSlug/admin/seasons" element={<AdminSeasons />} />
          <Route path="/venues/:venueSlug/admin/settings" element={<AdminSettings />} />
          {/* The TV's own settings sit apart from the venue's because they are
              a rank lower: PUT /settings/tv is venue_admin where /settings is
              venue_owner, so an admin who may upload the artwork may also set
              the timing it runs on. */}
          <Route path="/venues/:venueSlug/admin/tv" element={<AdminTvSettings />} />
          {/* Gated a second time inside the page on charity_enabled — the route
              exists for every venue, the page tells the ones not running a
              charity why there is nothing here. */}
          <Route path="/venues/:venueSlug/admin/charity" element={<AdminCharity />} />
        </Route>

        {/* ---- Platform administration — AppShell swaps its nav ---------- */}
        <Route element={<ProtectedRoute platform />}>
          <Route path="/admin/venues" element={<PlatformVenues />} />
          <Route path="/admin/venues/:venueId" element={<PlatformVenueDetail />} />
          <Route path="/admin/settings" element={<PlatformSettings />} />
        </Route>

        {/* Last, and inside the shell: an unknown path still gets the chrome
            and a way out. It carries no :venueSlug, which is why AppShell
            renders no venue nav when there is none. */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ---- No shell ----------------------------------------------------- */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      {/* Beside /login for the same reason: .hd-auth is a centred column with
          no chrome, and the sidebar has nothing to offer somebody who has no
          account yet. */}
      <Route path="/signup" element={<SignUp />} />
      {/* Where the verification email's link lands. UNGUARDED — see above. */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/board" element={<Board />} />
      <Route path="/venues/:venueSlug/scorer" element={<Scorer />} />

      {/* The phone half of board pairing. GUARDED, where /board is not: the
          tablet is asking to be paired and that grants it nothing, while this
          is the page that decides a venue, a night and a board, so it needs to
          know who is asking.

          The DEFAULT guard, not `venueAdmin` — that variant reads its venue
          from a :venueSlug param, and this route deliberately has none. Which
          venue is the question the page answers, from the account's own venue
          list. The admin check itself is the server's, in the pair endpoint.

          Both forms: /pair/:code from a scan, /pair bare for the typed
          fallback when a camera will not focus across a dim room. */}
      <Route element={<ProtectedRoute />}>
        <Route path="/pair" element={<Pair />} />
        <Route path="/pair/:code" element={<Pair />} />
      </Route>

      {/* The one full-screen surface behind a login. An admin opens it on a
          cast device or second screen; it is not paired to anything, so the
          session is the only thing that can authorise it. */}
      <Route element={<ProtectedRoute venueAdmin />}>
        <Route path="/venues/:venueSlug/tv" element={<Tv />} />
      </Route>
    </Routes>
  );
}
